import type { ComponentType } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import type {
  SurveysEnumsRespondentStrategyEnum,
  SurveysRespondentStrategyInput,
} from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'

import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { EmployeesStrategy } from 'modules/engagement_surveys/components/SurveyWizard/steps/ParticipantsStep/strategies/Employees'
import { LegalEntitiesStrategy } from 'modules/engagement_surveys/components/SurveyWizard/steps/ParticipantsStep/strategies/LegalEntities'
import { LocationsStrategy } from 'modules/engagement_surveys/components/SurveyWizard/steps/ParticipantsStep/strategies/Locations'
import { TeamsStrategy } from 'modules/engagement_surveys/components/SurveyWizard/steps/ParticipantsStep/strategies/Teams'

type OtherStrategyProps = {
  strategy: SurveysRespondentStrategyInput
  onChange: (ids: number[]) => void
}

type OtherStrategyKey = Exclude<
  SurveysEnumsRespondentStrategyEnum,
  'employees' | 'all_employees' | 'everyone' | 'managers'
>

const otherStrategyComponentMap: Record<OtherStrategyKey, ComponentType<OtherStrategyProps>> = {
  legal_entities: LegalEntitiesStrategy,
  locations: LocationsStrategy,
  teams: TeamsStrategy,
}

function isOtherStrategyKey(strategy: string): strategy is OtherStrategyKey {
  return strategy in otherStrategyComponentMap
}

interface ParticipantsSelectorProps {
  value: SurveysRespondentStrategyInput
  onChange: (respondentStrategy: SurveysRespondentStrategyInput) => void
  onEmployeeSelectionChange?: (selection: EmployeeSelectorV2MultiSelectState) => void
}

export function ParticipantsSelector({
  value,
  onChange,
  onEmployeeSelectionChange,
}: ParticipantsSelectorProps) {
  const isEmployeeStrategy = value.strategy === 'employees'
  const OtherStrategyComponent =
    !isEmployeeStrategy && isOtherStrategyKey(value.strategy)
      ? otherStrategyComponentMap[value.strategy]
      : undefined

  return (
    <Box gap="s16">
      <SelectField
        label={i18n.t('surveys.configuration.participants.label')}
        value={value.strategy}
        onChange={(strategy) => {
          onChange({
            // @ts-expect-error: Gamma select is not typed and we can't use assertions
            strategy,
            arguments: [],
          })
        }}
        options={[
          {
            label: i18n.t('surveys.configuration.participants.options.everyone.label'),
            value: 'everyone',
          },
          {
            label: i18n.t('surveys.configuration.participants.options.all_employees.label'),
            value: 'all_employees',
          },
          {
            label: i18n.t('surveys.configuration.participants.options.employees.label'),
            value: 'employees',
          },
          {
            label: i18n.t('surveys.configuration.participants.options.managers.label'),
            value: 'managers',
          },
          {
            label: i18n.t('surveys.configuration.participants.options.teams.label'),
            value: 'teams',
          },
          {
            label: i18n.t('surveys.configuration.participants.options.locations.label'),
            value: 'locations',
          },
          {
            label: i18n.t('surveys.configuration.participants.options.legal_entities.label'),
            value: 'legal_entities',
          },
        ]}
      />

      {isEmployeeStrategy && onEmployeeSelectionChange && (
        <EmployeesStrategy strategy={value} onChange={onEmployeeSelectionChange} />
      )}

      {OtherStrategyComponent && (
        <OtherStrategyComponent
          strategy={value}
          onChange={(ids) => {
            onChange({
              ...value,
              arguments: ids,
            })
          }}
        />
      )}
    </Box>
  )
}
